/**
 * Call Break (Nepali/Indian "Call Brake") — server-authoritative rules engine.
 *
 * Rules implemented (all toggles live in {@link CallBreakConfig}):
 *  - 4 players, one 52-card deck, 13 cards each, spades are always trump.
 *  - Each round every player calls (bids) between minBid and maxBid tricks, starting left of the dealer.
 *  - The player left of the dealer leads the first trick; the trick winner leads the next.
 *  - Players must follow the led suit. With `mustBeatIfPossible` they must also head the trick
 *    (play higher than the current best card of the led suit) when able and the trick is not yet trumped.
 *  - Void in the led suit: with `mustTrumpIfVoid` a spade must be played; with `mustOvertrumpIfPossible`
 *    the spade must beat any spade already in the trick when able, otherwise any card may be played.
 *  - Trick winner: highest spade, else highest card of the led suit.
 *  - Scoring (integers in tenths): made bid → bid·10 + overtricks; failed bid → −bid·10.
 *    `display: 'decimal'` renders 32 as 3.2 (Nepali style), `'points'` renders 32 as 32.
 *    Optional big-bid bonus: a made bid ≥ bigBidThreshold scores bigBidScore.
 *  - Misdeal/redeal when a hand has no spade (`redealIfNoSpade`) or no J/Q/K/A (`redealIfNoHonour`).
 *  - Match = `rounds` rounds, dealer rotates clockwise; highest total wins, ties share the place.
 */
import { buildDeck, parseCard, removeCards, sortHand, type CardId, type Suit } from '../../core/cards.js';
import { checkBool, checkEnum, checkInt, mergeKnown } from '../../core/config.js';
import { shuffle, type Rng } from '../../core/rng.js';
import {
  assertEngine,
  EngineError,
  isPlainObject,
  standingsFromScores,
  type EngineEvent,
  type GameEngine,
  type Standing,
} from '../../core/types.js';

export interface CallBreakConfig {
  rounds: number;
  minBid: number;
  maxBid: number;
  mustBeatIfPossible: boolean;
  mustTrumpIfVoid: boolean;
  mustOvertrumpIfPossible: boolean;
  redealIfNoSpade: boolean;
  redealIfNoHonour: boolean;
  /** null disables the bonus. */
  bigBidThreshold: number | null;
  bigBidScore: number;
  display: 'decimal' | 'points';
  /** Pause between rounds for the scoreboard. */
  roundEndDelayMs: number;
  /** Pause after the last card of a trick so players can see it. */
  trickEndDelayMs: number;
}

export type CallBreakPhase = 'BIDDING' | 'PLAYING' | 'TRICK_END' | 'ROUND_END' | 'MATCH_END';

export interface TrickPlay {
  seat: number;
  card: CardId;
}

export interface CompletedTrick {
  leader: number;
  plays: TrickPlay[];
  winner: number;
}

export interface CallBreakState {
  config: CallBreakConfig;
  players: string[];
  round: number;
  dealer: number;
  phase: CallBreakPhase;
  hands: CardId[][];
  bids: (number | null)[];
  tricksWon: number[];
  turn: number;
  trick: TrickPlay[];
  /** Completed tricks of the current round (public information). */
  tricks: CompletedTrick[];
  /** Per-round score rows in tenths, index = round-1. */
  roundScores: number[][];
  totals: number[];
  redeals: number;
}

export type CallBreakAction = { type: 'bid'; value: number } | { type: 'play'; card: CardId };

export type CallBreakLegal =
  | { kind: 'bid'; min: number; max: number }
  | { kind: 'play'; cards: CardId[] }
  | { kind: 'none' };

export interface CallBreakView {
  gameType: 'CALL_BREAK';
  config: CallBreakConfig;
  seat: number | null;
  phase: CallBreakPhase;
  round: number;
  dealer: number;
  turn: number;
  hand: CardId[];
  handCounts: number[];
  bids: (number | null)[];
  tricksWon: number[];
  trick: TrickPlay[];
  lastTrick: CompletedTrick | null;
  /** All cards played this round, in order — public knowledge, useful for card tracking. */
  tricks: CompletedTrick[];
  roundScores: number[][];
  totals: number[];
}

const DEFAULTS: CallBreakConfig = {
  rounds: 5,
  minBid: 1,
  maxBid: 13,
  mustBeatIfPossible: true,
  mustTrumpIfVoid: true,
  mustOvertrumpIfPossible: true,
  redealIfNoSpade: true,
  redealIfNoHonour: true,
  bigBidThreshold: null,
  bigBidScore: 130,
  display: 'decimal',
  roundEndDelayMs: 5000,
  trickEndDelayMs: 900,
};

const TRUMP: Suit = 'S';
const SEATS = 4;
const MAX_REDEALS = 20;

export function trickWinner(plays: readonly TrickPlay[]): number {
  const lead = parseCard(plays[0]!.card).suit;
  let best = plays[0]!;
  for (const p of plays.slice(1)) {
    const c = parseCard(p.card);
    const b = parseCard(best.card);
    const beats = (c.suit === TRUMP && b.suit !== TRUMP) || (c.suit === b.suit && c.rank > b.rank);
    if (beats && (c.suit === TRUMP || c.suit === lead)) best = p;
  }
  return best.seat;
}

export function legalCards(hand: readonly CardId[], trick: readonly TrickPlay[], cfg: CallBreakConfig): CardId[] {
  if (trick.length === 0) return [...hand];
  const lead = parseCard(trick[0]!.card).suit;
  const cards = hand.map(parseCard);
  const trumped = lead !== TRUMP && trick.some((p) => parseCard(p.card).suit === TRUMP);
  const ofLead = cards.filter((c) => c.suit === lead);
  if (ofLead.length > 0) {
    if (cfg.mustBeatIfPossible && !trumped) {
      const best = Math.max(...trick.map((p) => parseCard(p.card)).filter((c) => c.suit === lead).map((c) => c.rank));
      const higher = ofLead.filter((c) => c.rank > best);
      if (higher.length > 0) return higher.map((c) => c.id);
    }
    return ofLead.map((c) => c.id);
  }
  const spades = cards.filter((c) => c.suit === TRUMP);
  if (cfg.mustTrumpIfVoid && spades.length > 0) {
    if (!trumped) return spades.map((c) => c.id);
    if (cfg.mustOvertrumpIfPossible) {
      const bestSpade = Math.max(...trick.map((p) => parseCard(p.card)).filter((c) => c.suit === TRUMP).map((c) => c.rank));
      const higher = spades.filter((c) => c.rank > bestSpade);
      if (higher.length > 0) return higher.map((c) => c.id);
      return [...hand];
    }
    return spades.map((c) => c.id);
  }
  return [...hand];
}

export function scoreRound(bid: number, tricks: number, cfg: CallBreakConfig): number {
  if (tricks < bid) return -bid * 10;
  if (cfg.bigBidThreshold !== null && bid >= cfg.bigBidThreshold) return cfg.bigBidScore;
  return bid * 10 + (tricks - bid);
}

/** Heuristic bid used for timeouts (bots use their own, stronger estimators). */
export function simpleBidEstimate(hand: readonly CardId[], cfg: CallBreakConfig): number {
  const cards = hand.map(parseCard);
  let est = 0;
  for (const c of cards) {
    if (c.suit === TRUMP) est += c.rank >= 12 ? 1 : c.rank >= 10 ? 0.5 : 0;
    else if (c.rank === 14) est += 1;
    else if (c.rank === 13) est += cards.filter((x) => x.suit === c.suit).length <= 4 ? 0.6 : 0.2;
  }
  const spades = cards.filter((c) => c.suit === TRUMP).length;
  if (spades > 4) est += (spades - 4) * 0.8;
  return Math.min(cfg.maxBid, Math.max(cfg.minBid, Math.round(est)));
}

function needsRedeal(hands: CardId[][], cfg: CallBreakConfig): boolean {
  return hands.some((h) => {
    const cards = h.map(parseCard);
    if (cfg.redealIfNoSpade && !cards.some((c) => c.suit === TRUMP)) return true;
    if (cfg.redealIfNoHonour && !cards.some((c) => c.rank >= 11)) return true;
    return false;
  });
}

function deal(state: CallBreakState, rng: Rng, events: EngineEvent[]): void {
  for (let attempt = 0; attempt < MAX_REDEALS; attempt++) {
    const deck = shuffle(buildDeck(), rng);
    const hands: CardId[][] = [[], [], [], []];
    deck.forEach((c, i) => hands[i % SEATS]!.push(c));
    if (attempt < MAX_REDEALS - 1 && needsRedeal(hands, state.config)) {
      state.redeals++;
      events.push({ type: 'redeal', reason: 'misdeal' });
      continue;
    }
    state.hands = hands.map((h) => sortHand(h));
    break;
  }
  state.bids = [null, null, null, null];
  state.tricksWon = [0, 0, 0, 0];
  state.trick = [];
  state.tricks = [];
  state.phase = 'BIDDING';
  state.turn = (state.dealer + 1) % SEATS;
  events.push({ type: 'dealt', round: state.round, dealer: state.dealer, counts: state.hands.map((h) => h.length) });
}

export const callBreakEngine: GameEngine<CallBreakConfig, CallBreakState, CallBreakAction, CallBreakView, CallBreakLegal> = {
  gameType: 'CALL_BREAK',
  kind: 'match',

  defaultConfig: () => ({ ...DEFAULTS }),

  normalizeConfig(input) {
    const c = mergeKnown(DEFAULTS, input);
    checkInt('rounds', c.rounds, 1, 20);
    checkInt('minBid', c.minBid, 1, 13);
    checkInt('maxBid', c.maxBid, c.minBid, 13);
    checkBool('mustBeatIfPossible', c.mustBeatIfPossible);
    checkBool('mustTrumpIfVoid', c.mustTrumpIfVoid);
    checkBool('mustOvertrumpIfPossible', c.mustOvertrumpIfPossible);
    checkBool('redealIfNoSpade', c.redealIfNoSpade);
    checkBool('redealIfNoHonour', c.redealIfNoHonour);
    if (c.bigBidThreshold !== null) checkInt('bigBidThreshold', c.bigBidThreshold, c.minBid, 13);
    checkInt('bigBidScore', c.bigBidScore, 0, 1000);
    checkEnum('display', c.display, ['decimal', 'points'] as const);
    checkInt('roundEndDelayMs', c.roundEndDelayMs, 0, 60000);
    checkInt('trickEndDelayMs', c.trickEndDelayMs, 0, 10000);
    return c;
  },

  seatLimits: () => ({ min: SEATS, max: SEATS }),

  createState(config, seats, rng) {
    assertEngine(seats.length === SEATS, 'INVALID_CONFIG', 'Call Break needs exactly 4 seats');
    const players = Array<string>(SEATS).fill('');
    for (const s of seats) {
      assertEngine(s.seat >= 0 && s.seat < SEATS && !players[s.seat], 'SEAT_TAKEN', `Bad seat ${s.seat}`);
      players[s.seat] = s.playerId;
    }
    const state: CallBreakState = {
      config,
      players,
      round: 1,
      dealer: rng.int(SEATS),
      phase: 'BIDDING',
      hands: [],
      bids: [],
      tricksWon: [],
      turn: 0,
      trick: [],
      tricks: [],
      roundScores: [],
      totals: [0, 0, 0, 0],
      redeals: 0,
    };
    deal(state, rng, []);
    return state;
  },

  parseAction(raw) {
    if (!isPlainObject(raw)) throw new EngineError('INVALID_ACTION', 'Action must be an object');
    if (raw.type === 'bid' && typeof raw.value === 'number' && Number.isInteger(raw.value)) {
      return { type: 'bid', value: raw.value };
    }
    if (raw.type === 'play' && typeof raw.card === 'string' && raw.card.length <= 4) return { type: 'play', card: raw.card };
    throw new EngineError('INVALID_ACTION', 'Unknown Call Break action');
  },

  applyAction(state, seat, action) {
    assertEngine(state.phase !== 'MATCH_END', 'GAME_OVER', 'Match is over');
    assertEngine(seat === state.turn && (state.phase === 'BIDDING' || state.phase === 'PLAYING'), 'NOT_YOUR_TURN', 'Not your turn');
    const cfg = state.config;
    const events: EngineEvent[] = [];
    if (action.type === 'bid') {
      assertEngine(state.phase === 'BIDDING', 'INVALID_PHASE', 'Bidding is closed');
      assertEngine(action.value >= cfg.minBid && action.value <= cfg.maxBid, 'INVALID_AMOUNT', 'Bid out of range');
      state.bids[seat] = action.value;
      events.push({ type: 'bid', seat, value: action.value });
      state.turn = (seat + 1) % SEATS;
      if (state.bids.every((b) => b !== null)) {
        state.phase = 'PLAYING';
        state.turn = (state.dealer + 1) % SEATS;
        events.push({ type: 'biddingComplete', bids: [...state.bids] });
      }
      return events;
    }
    assertEngine(state.phase === 'PLAYING', 'INVALID_PHASE', 'Not in play phase');
    const hand = state.hands[seat]!;
    assertEngine(hand.includes(action.card), 'INVALID_CARD', 'Card not in hand');
    assertEngine(legalCards(hand, state.trick, cfg).includes(action.card), 'ILLEGAL_MOVE', 'That card is not allowed now');
    removeCards(hand, [action.card]);
    state.trick.push({ seat, card: action.card });
    events.push({ type: 'cardPlayed', seat, card: action.card });
    if (state.trick.length < SEATS) {
      state.turn = (seat + 1) % SEATS;
      return events;
    }
    const winner = trickWinner(state.trick);
    state.tricksWon[winner]!++;
    state.tricks.push({ leader: state.trick[0]!.seat, plays: state.trick, winner });
    events.push({ type: 'trickWon', seat: winner, trickNo: state.tricks.length });
    state.phase = 'TRICK_END';
    state.turn = winner;
    return events;
  },

  getActors(state) {
    return state.phase === 'BIDDING' || state.phase === 'PLAYING' ? [state.turn] : [];
  },

  getLegalActions(state, seat) {
    if (seat !== state.turn) return { kind: 'none' };
    if (state.phase === 'BIDDING') return { kind: 'bid', min: state.config.minBid, max: state.config.maxBid };
    if (state.phase === 'PLAYING') return { kind: 'play', cards: legalCards(state.hands[seat]!, state.trick, state.config) };
    return { kind: 'none' };
  },

  getTimeoutAction(state, seat) {
    if (state.phase === 'BIDDING') return { type: 'bid', value: simpleBidEstimate(state.hands[seat]!, state.config) };
    const legal = legalCards(state.hands[seat]!, state.trick, state.config);
    // Lowest legal card: rarely wastes a winner, never illegal.
    const lowest = [...legal].sort((a, b) => {
      const ca = parseCard(a);
      const cb = parseCard(b);
      return (ca.suit === TRUMP ? 100 : 0) + ca.rank - ((cb.suit === TRUMP ? 100 : 0) + cb.rank);
    })[0]!;
    return { type: 'play', card: lowest };
  },

  getView(state, seat) {
    return {
      gameType: 'CALL_BREAK',
      config: state.config,
      seat,
      phase: state.phase,
      round: state.round,
      dealer: state.dealer,
      turn: state.turn,
      hand: seat === null ? [] : [...state.hands[seat]!],
      handCounts: state.hands.map((h) => h.length),
      bids: [...state.bids],
      tricksWon: [...state.tricksWon],
      trick: state.phase === 'TRICK_END' ? [...(state.tricks.at(-1)?.plays ?? [])] : [...state.trick],
      lastTrick: state.tricks.at(-1) ?? null,
      tricks: state.tricks.map((t) => ({ ...t, plays: [...t.plays] })),
      roundScores: state.roundScores.map((r) => [...r]),
      totals: [...state.totals],
    };
  },

  getSystemStep(state) {
    if (state.phase === 'TRICK_END') return { kind: 'collectTrick', delayMs: state.config.trickEndDelayMs };
    if (state.phase === 'ROUND_END') return { kind: 'nextRound', delayMs: state.config.roundEndDelayMs };
    return null;
  },

  runSystemStep(state, rng) {
    const events: EngineEvent[] = [];
    if (state.phase === 'TRICK_END') {
      state.trick = [];
      if (state.tricks.length < 13) {
        state.phase = 'PLAYING';
        events.push({ type: 'trickCollected', leader: state.turn });
        return events;
      }
      const scores = state.bids.map((b, i) => scoreRound(b ?? 0, state.tricksWon[i]!, state.config));
      state.roundScores.push(scores);
      state.totals = state.totals.map((t, i) => t + scores[i]!);
      events.push({ type: 'roundEnd', round: state.round, scores, totals: [...state.totals], tricksWon: [...state.tricksWon] });
      if (state.round >= state.config.rounds) {
        state.phase = 'MATCH_END';
        events.push({ type: 'matchEnd', standings: standingsFromScores(state.totals.map((score, seat) => ({ seat, score }))) });
      } else {
        state.phase = 'ROUND_END';
      }
      return events;
    }
    if (state.phase === 'ROUND_END') {
      state.round++;
      state.dealer = (state.dealer + 1) % SEATS;
      deal(state, rng, events);
      return events;
    }
    return events;
  },

  isFinished: (state) => state.phase === 'MATCH_END',

  getStandings(state): Standing[] {
    return standingsFromScores(state.totals.map((score, seat) => ({ seat, score })));
  },

  getRoundsCompleted: (state) => state.roundScores.length,
};

export function formatCallBreakScore(tenths: number, display: CallBreakConfig['display']): string {
  if (display === 'points') return String(tenths);
  const sign = tenths < 0 ? '-' : '';
  const abs = Math.abs(tenths);
  // Overtricks are counted in tenths but can exceed 9 (e.g. bid 1, 12 extra → 1 + 1.2 = 2.2).
  return `${sign}${Math.floor(abs / 10)}.${abs % 10}`;
}
